import React, { useEffect, useState, useCallback } from 'react'
import { useParams, useHistory } from 'react-router-dom'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import styles from './GuestProfilePage.module.scss'
import outreachService from '../../../supabaseServices/OutreachService'

interface RouteParams {
  uuid: string
}

interface GuestProfile {
  uuid: string
  first_name: string | null
  last_name: string | null
  email: string | null
  phone: string | null
  city: string | null
  photo_url: string | null
  created_at: string
}

interface OutreachRecord {
  uuid: string
  email_subject: string
  destination: string | null
  status: string
  created_at: string
  sent_at: string | null
  trip_start_date: string | null
}

interface TripRecord {
  trips: {
    uuid: string
    trip_name: string
    trip_type: string
    location: string
    start_date: string
    end_date: string
    status: string
  }
}

function GuestProfilePage() {
  const { uuid } = useParams<RouteParams>()
  const history = useHistory()

  const [profile, setProfile] = useState<GuestProfile | null>(null)
  const [outreachHistory, setOutreachHistory] = useState<OutreachRecord[]>([])
  const [trips, setTrips] = useState<TripRecord[]>([])
  const [loading, setLoading] = useState<boolean>(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    const [profileData, outreachData, tripData] = await Promise.all([
      outreachService.getGuestProfile(uuid),
      outreachService.getOutreachByRecipient(uuid),
      outreachService.getGuestTrips(uuid),
    ])
    setProfile(profileData)
    setOutreachHistory(outreachData)
    setTrips(tripData)
    setLoading(false)
  }, [uuid])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return 'N/A'
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    } catch {
      return dateStr
    }
  }

  const getStatusClass = (status: string): string => {
    switch (status) {
      case 'DRAFT':
        return styles.statusDraft
      case 'SENT':
        return styles.statusSent
      case 'SKIPPED':
        return styles.statusSkipped
      default:
        return ''
    }
  }

  const getInitials = (profile: GuestProfile): string => {
    const first: string = profile.first_name?.[0] || ''
    const last: string = profile.last_name?.[0] || ''
    return (first + last).toUpperCase() || '?'
  }

  if (loading) {
    return <div className={styles.loading}>Loading...</div>
  }

  if (!profile) {
    return <div className={styles.loading}>Guest not found.</div>
  }

  const fullName: string =
    [profile.first_name, profile.last_name].filter(Boolean).join(' ') || 'Unknown'

  return (
    <div className={styles.mainWrapper}>
      <button className={styles.backButton} onClick={() => history.push('/outreach')}>
        <ArrowBackIcon fontSize="small" /> Back to Outreach
      </button>

      <div className={styles.header}>
        <div className={styles.avatar}>
          {profile.photo_url ? (
            <img src={profile.photo_url} alt={fullName} />
          ) : (
            getInitials(profile)
          )}
        </div>
        <div className={styles.headerInfo}>
          <h1 className={styles.name}>{fullName}</h1>
          <p className={styles.email}>{profile.email || 'No email'}</p>
        </div>
      </div>

      <div className={styles.infoGrid}>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Phone</span>
          <span className={styles.infoValue}>{profile.phone || 'N/A'}</span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>City</span>
          <span className={styles.infoValue}>{profile.city || 'N/A'}</span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Member Since</span>
          <span className={styles.infoValue}>{formatDate(profile.created_at)}</span>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoLabel}>Trips</span>
          <span className={styles.infoValue}>{trips.length}</span>
        </div>
      </div>

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Outreach History</h2>
        {outreachHistory.length === 0 ? (
          <div className={styles.emptyState}>No outreach emails for this guest.</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Subject</th>
                <th>Destination</th>
                <th>Status</th>
                <th>Created</th>
                <th>Sent</th>
              </tr>
            </thead>
            <tbody>
              {outreachHistory.map((record: OutreachRecord) => (
                <tr
                  key={record.uuid}
                  className={styles.clickableRow}
                  onClick={() => history.push(`/outreach/${record.uuid}`)}
                >
                  <td>{record.email_subject}</td>
                  <td>{record.destination || 'N/A'}</td>
                  <td>
                    <span className={`${styles.statusBadge} ${getStatusClass(record.status)}`}>
                      {record.status}
                    </span>
                  </td>
                  <td>{formatDate(record.created_at)}</td>
                  <td>{formatDate(record.sent_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>Trips</h2>
        {trips.length === 0 ? (
          <div className={styles.emptyState}>No trips found for this guest.</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Trip Name</th>
                <th>Type</th>
                <th>Location</th>
                <th>Start</th>
                <th>End</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {trips.map((tripUser: TripRecord, index: number) => (
                <tr key={index}>
                  <td>{tripUser.trips?.trip_name || 'N/A'}</td>
                  <td>{tripUser.trips?.trip_type || 'N/A'}</td>
                  <td>{tripUser.trips?.location || 'N/A'}</td>
                  <td>{formatDate(tripUser.trips?.start_date)}</td>
                  <td>{formatDate(tripUser.trips?.end_date)}</td>
                  <td>{tripUser.trips?.status || 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

export default GuestProfilePage
